import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useLoadStoreFromSalesChannelIdIntoRedux from '@fd/customHooks/useLoadStoreFromSalesChannelIdIntoRedux';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import { SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { flagService } from 'services';

import SettingsList, { SettingsMenuItem } from '@fd/ui/List/SettingsList';

import { getSelectedStore } from '../../selectors/store.selector';
import { getFlipdishSalesChannelMenuItems } from './utils/getFlipdishSalesChannelMenuItems';

type FlipdishSalesChannelSettingsListProps = {
  baseUrl: string;
  salesChannelType: SalesChannelType;
  linkedSalesChannelId?: string;
};

const FlipdishSalesChannelSettingsList = (props: FlipdishSalesChannelSettingsListProps) => {
  const { baseUrl, salesChannelType, linkedSalesChannelId } = props;
  const appId = useAppSelector((state) => state.currentApp.AppId) ?? '';
  const permissions = useAppSelector((state) => state.permissions);
  const { propertyId, channelId } = useParams<{ channelId: string; propertyId: string }>();
  const { data: rmsCommonData, isLoading: isLoadingRMSCommonData } =
    useQueryRMSCommonDataHook(appId);

  const isOldStorePage = window.location?.pathname?.includes('/stores');
  useLoadStoreFromSalesChannelIdIntoRedux({});
  const store = useAppSelector(getSelectedStore);

  const isShowCollectionSettingsAtPropertyLevelOn = useAppSelector((state) =>
    flagService.isFlagOn(state, 'showCollectionSettingsAtPropertyLevel')
  );

  const { data: storefrontInformation } = useQuery({
    queryKey: ['getStoreInformation', baseUrl, salesChannelType, propertyId, channelId],
    queryFn: async () => {
      const storeFront = rmsCommonData?.associations?.orgHierarchy.properties
        .find((property) => property.code === propertyId)
        ?.storefronts.find((storefront) => storefront.code === channelId);

      if (!storeFront) {
        return {
          name: '',
          code: channelId,
        };
      }

      return {
        name: storeFront.name,
        code: storeFront.code,
      };
    },
    enabled: !isLoadingRMSCommonData && !!rmsCommonData?.associations,
  });

  const menuItems = getFlipdishSalesChannelMenuItems({
    appId,
    baseUrl,
    salesChannelType,
    storefrontInformation,
    linkedSalesChannelId,
    isOldStorePage,
    permissions,
    isShowCollectionSettingsAtPropertyLevelOn,
    supportedSalesChannelTypes: store?.SalesChannelTypes,
    storeId: store?.StoreId ? String(store.StoreId) : undefined,
  });

  const shouldShowAsLast = (item: SettingsMenuItem, index: number) => {
    // The danger zone should always be shown as the last item
    if (item.title === 'Danger_zone_title') {
      return true;
    }

    // For other items, check if it's the last visible item
    return index === menuItems.length - 1;
  };

  return <SettingsList menuItems={menuItems} shouldShowAsLast={shouldShowAsLast} />;
};

export default FlipdishSalesChannelSettingsList;
